import { Text, Icon, Spinner, Flex } from "components";
import { useGetExportResultLazyQuery } from "generated/graphql";
import { useEffect, useState } from "react";
import { useRecoilState } from "recoil";
import { asyncJobMenuState, asyncJobTaskState } from "state/asyncJobState";
import { motion } from "framer-motion";
import cn from "classnames";
import styles from "./AsyncJobStatusMenu.module.scss";

export const AsyncJobStatusMenu: React.FC = () => {
  const [menuState, setMenuState] = useRecoilState(asyncJobMenuState);
  const [taskId, setTaskId] = useRecoilState(asyncJobTaskState);
  const [getExportResult, { data, stopPolling, error }] = useGetExportResultLazyQuery();
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (error) {
      stopPolling();
      setIsLoading(false);
      setTaskId(undefined);
    }
  }, [error, setTaskId, stopPolling]);

  useEffect(() => {
    if (taskId) {
      setIsLoading(true);

      getExportResult({
        variables: {
          id: taskId,
        },
        pollInterval: 5000,
      });
    }
    if (data?.exportResult?.isReady) {
      stopPolling();
      setIsLoading(false);
    }

    if (taskId && data?.exportResult?.url && data?.exportResult?.url.includes(taskId)) {
      window.location.assign(data?.exportResult?.url);
      setTaskId(undefined);
    }

    return () => {
      stopPolling();
    };
  }, [data?.exportResult?.isReady, data?.exportResult?.url, getExportResult, setTaskId, stopPolling, taskId]);

  const handleClick = () => {
    if (menuState === "collapsed") {
      setMenuState("expanded");
    } else {
      setMenuState("collapsed");
    }
  };

  return (
    <motion.div
      layout
      className={cn(styles.statusMenu, {
        [styles["statusMenu--collapsed"]]: menuState === "collapsed",
        [styles["statusMenu--expanded"]]: menuState === "expanded",
      })}
    >
      <Flex container containerJustifyContent="space-between" containerAlignItems="center" className={styles.menuBar}>
        <Flex container containerAlignItems="center">
          <Spacer width={16} />
          <Text smaller colour="white" className={styles.activityText}>
            Activity
          </Text>
          <Spacer width={16} />
          <StatusBadge isLoading={isLoading} isError={Boolean(error)} />
        </Flex>

        <Flex container containerJustifyContent="flex-end" containerAlignItems="center">
          <div className={styles.pointer} onClick={handleClick}>
            <Icon name={menuState === "collapsed" ? "maximize" : "minimize"} />
          </div>
          <Spacer width={26} />
          <div className={styles.pointer} onClick={() => setMenuState("closed")}>
            <Icon size="tiny" name="cross" />
          </div>
          <Spacer width={16} />
        </Flex>
      </Flex>
      <ul className={styles.activityList}>
        <JobMenuItem isLoading={isLoading} isError={Boolean(error)} />
      </ul>
    </motion.div>
  );
};

const StatusBadge = ({ isLoading, isError }: { isLoading: boolean; isError: boolean }) => {
  return (
    <div
      className={cn(styles.badge, {
        [styles["badge--loading"]]: isLoading,
        [styles["badge--error"]]: isError,
      })}
    >
      <Text
        className={cn(styles.badgeText, {
          [styles["badgeText--loading"]]: isLoading,
          [styles["badgeText--error"]]: isError,
        })}
        smaller
      >
        {isLoading ? "In Progress" : isError ? "Error" : "Complete"}
      </Text>
    </div>
  );
};

const JobMenuItem = ({ isLoading, isError }: { isLoading: boolean; isError: boolean }) => {
  return (
    <>
      <Spacer height={16} />
      <li className={styles.menuItem}>
        <Flex container containerAlignItems="center">
          <div className={styles.iconBackground}>
            <Icon name="downloadFile" size="medium" />
          </div>
          <Text colour="black" smallest style={{ fontWeight: 400 }}>
            {isLoading ? "Downloading CSV" : isError ? "CSV Download Failed" : "CSV Download Complete"}
          </Text>
        </Flex>

        <div
          className={cn(styles.iconBackground, styles["iconBackground--status-color"], {
            [styles["iconBackground--status-color--loading"]]: isLoading,
            [styles["iconBackground--status-color--error"]]: isError,
          })}
        >
          {isLoading ? (
            <Spinner style={{ height: "70%", width: "70%" }} />
          ) : isError ? (
            <Icon colour="red" name="warning" />
          ) : (
            <Icon colour="white" name="check" />
          )}
        </div>
      </li>
      <Spacer height={16} />
    </>
  );
};

const Spacer = ({ width, height }: { width?: number; height?: number }) => {
  return <div style={{ width, height }} />;
};
